import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import { Box, Typography } from '@mui/material';

import type { DetailedCircuitGeometry, TrackContributionGpsCoord } from '@kangaroo/shared';
import { splitContinuousTelemetryPoints } from '@kangaroo/shared';

import type { ReferenceTrackGuideColors } from '../../lib/osmTrackOverlay';
import { sessionMapDebug } from '../../lib/sessionMapDebug';
import {
  trackCatalogSegmentColor,
  trackCatalogSegmentVisible,
  type TrackCatalogMapLayers,
} from '../../lib/trackCatalog';
import {
  buildReplayMotionCache,
  interpolateMapPointAtTimeWithHint,
  sampleReplayMotionCache,
  speedColorBrakeToFlatOut,
  trackSpeedRange,
  type InterpolateMapPointHint,
  type MapReplayCursor,
  type ReplayMotionCache,
  type TrackMapMarker,
  type TrackMapPoint,
} from '../../lib/telemetrySession';
import { subscribeToPlayheadTicks } from '../../lib/useTelemetryReplay';

import 'mapbox-gl/dist/mapbox-gl.css';

type MapboxTrackMapProps = {
  accessToken: string;
  points: TrackMapPoint[];
  trackGroups?: TrackMapPoint[][];
  referenceTrackGroups?: TrackMapPoint[][];
  speedRange?: { min: number; max: number };
  height?: number;
  title?: string;
  fill?: boolean;
  resetViewKey?: string | number;
  onMapClick?: () => void;
  onMapLatLngClick?: (coord: { lat: number; lon: number }) => void;
  onMapLatLngHover?: (coord: { lat: number; lon: number } | null) => void;
  crosshairCursor?: boolean;
  freehandDrawActive?: boolean;
  onMapLatLngDrawStroke?: (coord: { lat: number; lon: number }) => void;
  onMapLatLngDrawEnd?: () => void;
  highlightLines?: Array<{
    id: string;
    points: Array<{ lat: number; lon: number }>;
    color?: string;
    width?: number;
    opacity?: number;
    dash?: number[];
  }>;
  onMapMarkerClick?: (markerId: string) => void;
  onMapMarkerDragEnd?: (markerId: string, coord: { lat: number; lon: number }) => void;
  onOverlaysReady?: () => void;
  startFinishLine?: { pointA: { lat: number; lon: number }; pointB: { lat: number; lon: number } };
  crossTrackLines?: Array<{
    id: string;
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
    color?: string;
    draggable?: boolean;
    selected?: boolean;
    label?: string;
    /** Thin catalog divider (no pill halo). Default `pill`. */
    lineStyle?: 'pill' | 'catalog';
  }>;
  mapMarkers?: TrackMapMarker[];
  mapMarkerScale?: number;
  heatLineWidth?: number;
  heatLineHaloWidth?: number;
  fitBoundsPadding?: number;
  onTilesReady?: () => void;
  onLoadError?: (message: string) => void;
  mapViewport?: { lat: number; lon: number; zoom?: number };
  autoFitBounds?: boolean;
  /** When true, recenter on mapViewport changes (location search). Off while outlining. */
  followMapViewport?: boolean;
  referenceTrackStyle?: 'default' | 'guide';
  referenceTrackGuideColors?: ReferenceTrackGuideColors[];
  detailedCircuitPreview?: DetailedCircuitGeometry | null;
  selectedDetailedFeatureId?: string;
  persistentDetailedFeatureIds?: string[];
  /** When true, straight/corner divider lines are omitted from the preview (rendered as draggable crossTrackLines instead). */
  interactiveSectionDividers?: boolean;
  selectedSectionBoundaryLine?: {
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
  } | null;
  corridorOutlineRings?: TrackContributionGpsCoord[][];
  showLapLines?: boolean;
  idealPoints?: TrackMapPoint[];
  showIdealLine?: boolean;
  cursorTimecodeMs?: number | null;
  replayCursors?: MapReplayCursor[];
  followReplayCursor?: boolean;
  resolveReplayCursorTimecodeMsRef?: MutableRefObject<
    ((cursor: MapReplayCursor) => number | null) | undefined
  >;
};

const FOLLOW_ZOOM = 18;
const OVERLAY_PREFIX = 'paddiq-';
const FOLLOW_CAMERA_MIN_INTERVAL_MS = 16;

const DEFAULT_REFERENCE_GUIDE_COLORS: ReferenceTrackGuideColors = {
  line: '#94A3B8',
  arrow: '#E2E8F0',
  arrowOutline: '#07111C',
};

const DEFAULT_CIRCUIT_PREVIEW_LAYERS: TrackCatalogMapLayers = {
  trackOutline: true,
  corners: true,
  straights: true,
  sectors: true,
  lines: true,
  labels: false,
};

let overlayCounter = 0;

function nextOverlayId(kind: string) {
  overlayCounter += 1;
  return `${OVERLAY_PREFIX}${kind}-${overlayCounter}`;
}

function lngLatFromPoint(point: { lat: number; lon: number }): [number, number] {
  return [point.lon, point.lat];
}

function lngLatPath(points: TrackMapPoint[] | TrackContributionGpsCoord[]) {
  return points.map((point) => lngLatFromPoint(point));
}

function clearPaddiqOverlays(map: mapboxgl.Map) {
  const style = map.getStyle();
  if (!style) {
    return;
  }

  for (const layer of [...(style.layers ?? [])].reverse()) {
    if (layer.id.startsWith(OVERLAY_PREFIX)) {
      map.removeLayer(layer.id);
    }
  }

  for (const sourceId of Object.keys(style.sources ?? {})) {
    if (sourceId.startsWith(OVERLAY_PREFIX)) {
      map.removeSource(sourceId);
    }
  }
}

function addLineOverlay(
  map: mapboxgl.Map,
  coordinates: Array<[number, number]>,
  options: {
    color: string;
    width: number;
    opacity?: number;
    dash?: number[];
  },
) {
  if (coordinates.length < 2) {
    return;
  }

  const sourceId = nextOverlayId('line-src');
  const layerId = nextOverlayId('line');
  map.addSource(sourceId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates },
      properties: {},
    },
  });
  map.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': options.color,
      'line-width': options.width,
      'line-opacity': options.opacity ?? 1,
      ...(options.dash ? { 'line-dasharray': options.dash } : {}),
    },
  });
}

function addPolygonOverlay(
  map: mapboxgl.Map,
  rings: Array<[number, number][]>,
  options: {
    fillColor: string;
    fillOpacity?: number;
    strokeColor: string;
    strokeOpacity?: number;
    strokeWidth?: number;
  },
) {
  const closedRings = rings.filter((ring) => ring.length >= 3);
  if (closedRings.length === 0) {
    return;
  }

  const sourceId = nextOverlayId('poly-src');
  const fillLayerId = nextOverlayId('poly-fill');
  const lineLayerId = nextOverlayId('poly-line');
  map.addSource(sourceId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: closedRings },
      properties: {},
    },
  });
  map.addLayer({
    id: fillLayerId,
    type: 'fill',
    source: sourceId,
    paint: {
      'fill-color': options.fillColor,
      'fill-opacity': options.fillOpacity ?? 0.3,
    },
  });
  if ((options.strokeOpacity ?? 1) > 0 && (options.strokeWidth ?? 2) > 0) {
    map.addLayer({
      id: lineLayerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': options.strokeColor,
        'line-opacity': options.strokeOpacity ?? 1,
        'line-width': options.strokeWidth ?? 2,
      },
    });
  }
}

function drawCorridorOutlineRings(map: mapboxgl.Map, rings: TrackContributionGpsCoord[][]) {
  const paths = rings.filter((ring) => ring.length >= 3).map((ring) => lngLatPath(ring));
  if (paths.length > 0) {
    addPolygonOverlay(map, paths, {
      fillColor: '#ffffff',
      fillOpacity: 0.08,
      strokeColor: '#ffffff',
      strokeOpacity: 0.85,
      strokeWidth: 2,
    });
  }
}

function drawDetailedCircuitPreview(
  map: mapboxgl.Map,
  geometry: DetailedCircuitGeometry,
  layers: TrackCatalogMapLayers = DEFAULT_CIRCUIT_PREVIEW_LAYERS,
  options?: {
    selectedBoundaryLine?: {
      pointA: { lat: number; lon: number };
      pointB: { lat: number; lon: number };
    };
    interactiveSectionDividers?: boolean;
    selectedFeatureId?: string;
    persistentFeatureIds?: string[];
  },
) {
  if (layers.trackOutline && geometry.outlineRings.length > 0) {
    const paths = geometry.outlineRings
      .filter((ring) => ring.length >= 3)
      .map((ring) => lngLatPath(ring));
    if (paths.length > 0) {
      addPolygonOverlay(map, paths, {
        fillColor: '#ffffff',
        fillOpacity: 0.1,
        strokeColor: '#ffffff',
        strokeOpacity: 0.85,
        strokeWidth: 2,
      });
    }
  }

  const orderedFeatures = [...geometry.features].sort((left, right) => {
    const rank = (type: string) => (type.toLowerCase() === 'sector' ? 0 : 1);
    return rank(left.type) - rank(right.type);
  });
  const cornerBoundaryKeys = new Set(
    orderedFeatures
      .filter((feature) => feature.type.toLowerCase() === 'corner')
      .flatMap((feature) => [feature.entryLine, feature.exitLine])
      .filter((path): path is NonNullable<typeof path> => Boolean(path && path.length >= 2))
      .map((path) => JSON.stringify(path)),
  );

  for (const feature of orderedFeatures) {
    if (!trackCatalogSegmentVisible(feature.type, layers)) {
      continue;
    }

    const featureType = feature.type.toLowerCase();
    if (featureType === 'straight') {
      continue;
    }
    const isSelectedCorner =
      featureType === 'corner' &&
      Boolean(options?.selectedFeatureId) &&
      (feature.id === options?.selectedFeatureId ||
        feature.id.startsWith(`${options?.selectedFeatureId}-part-`));
    const isPersistentCorner =
      featureType === 'corner' &&
      Boolean(
        options?.persistentFeatureIds?.some(
          (id) => feature.id === id || feature.id.startsWith(`${id}-part-`),
        ),
      );
    if (featureType === 'corner' && !isSelectedCorner && !isPersistentCorner) {
      continue;
    }
    const paths = feature.polygonRings ?? [];
    if (paths.length === 0) {
      continue;
    }

    const color = trackCatalogSegmentColor(feature.type);
    const isSector = featureType === 'sector';
    const isCorner = featureType === 'corner';
    addPolygonOverlay(
      map,
      paths.map((ring) => lngLatPath(ring)),
      {
        fillColor: color,
        fillOpacity: isSector ? 0.11 : isSelectedCorner ? 0.64 : isCorner ? 0.46 : 0.34,
        strokeColor: color,
        strokeOpacity: isSector ? 0.6 : isCorner ? 0 : 0.95,
        strokeWidth: isSector ? 1.5 : isCorner ? 0 : 2,
      },
    );
  }

  if (geometry.centerLine && geometry.centerLine.length > 1) {
    addLineOverlay(map, lngLatPath(geometry.centerLine), {
      color: '#28c8ff',
      width: 2,
      opacity: 0.88,
    });
  }

  if (layers.lines) {
    const seen = new Set<string>();
    for (const feature of orderedFeatures) {
      if (!trackCatalogSegmentVisible(feature.type, layers)) {
        continue;
      }

      const featureType = feature.type.toLowerCase();
      if (featureType === 'straight') {
        continue;
      }

      const color = trackCatalogSegmentColor(feature.type);
      const isStartFinish = featureType === 'startfinish';
      const isKerb = featureType === 'kerbmarker';
      const isAuthoredBoundary =
        featureType === 'trackoutsideboundary' || featureType === 'trackinsideboundary';
      const skipSectionDivider = options?.interactiveSectionDividers && !isStartFinish;
      for (const path of [feature.entryLine, feature.exitLine]) {
        if (!path || path.length < 2 || skipSectionDivider) {
          continue;
        }

        if (isAuthoredBoundary) {
          addLineOverlay(map, lngLatPath(path), {
            color: featureType === 'trackoutsideboundary' ? '#F8FAFC' : '#CBD5E1',
            width: 2.5,
            opacity: 0.95,
          });
          continue;
        }

        if (isKerb) {
          continue;
        }

        const key = JSON.stringify(path);
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);

        drawCatalogDividerLine(
          map,
          { lat: path[0]!.lat, lon: path[0]!.lon },
          { lat: path[1]!.lat, lon: path[1]!.lon },
          isStartFinish ? '#ffe66d' : cornerBoundaryKeys.has(key) ? '#ff5f6d' : color,
          { startFinish: isStartFinish },
        );
      }
    }
  }

  if (options?.selectedBoundaryLine) {
    drawCatalogDividerLine(
      map,
      options.selectedBoundaryLine.pointA,
      options.selectedBoundaryLine.pointB,
      '#ffe66d',
      { selected: true },
    );
  }
}

function drawDetailedCircuitKerbs(map: mapboxgl.Map, geometry: DetailedCircuitGeometry) {
  for (const feature of geometry.features) {
    if (
      feature.type.toLowerCase() !== 'kerbmarker' ||
      !feature.entryLine ||
      feature.entryLine.length < 2
    ) {
      continue;
    }
    addLineOverlay(map, lngLatPath(feature.entryLine), {
      color: trackCatalogSegmentColor(feature.type),
      width: 11,
      opacity: 0.72,
    });
  }
}

function drawReferenceTrackOutlines(
  map: mapboxgl.Map,
  referenceTrackGroups: TrackMapPoint[][],
  style: 'default' | 'guide' = 'default',
  guideColors: ReferenceTrackGuideColors[] = [],
) {
  for (let groupIndex = 0; groupIndex < referenceTrackGroups.length; groupIndex += 1) {
    const group = referenceTrackGroups[groupIndex]!;
    const trackPath = lngLatPath(group);
    if (trackPath.length < 2) {
      continue;
    }

    if (style === 'guide') {
      const colors = guideColors[groupIndex] ?? DEFAULT_REFERENCE_GUIDE_COLORS;
      addLineOverlay(map, trackPath, {
        color: colors.line,
        width: 3,
        opacity: 0.95,
        dash: [2, 10],
      });
      continue;
    }

    addLineOverlay(map, trackPath, { color: '#07111C', width: 7, opacity: 0.88 });
    addLineOverlay(map, trackPath, { color: '#CBD5E1', width: 3.5, opacity: 0.72 });
  }
}

function trackGroupFingerprint(groups: TrackMapPoint[][]) {
  return groups
    .map((group) => {
      const first = group[0];
      const last = group.at(-1);
      return [
        group.length,
        first?.lat.toFixed(7) ?? '',
        first?.lon.toFixed(7) ?? '',
        last?.lat.toFixed(7) ?? '',
        last?.lon.toFixed(7) ?? '',
      ].join(',');
    })
    .join('|');
}

type CrossTrackLineSpec = NonNullable<MapboxTrackMapProps['crossTrackLines']>[number];

function drawCatalogDividerLine(
  map: mapboxgl.Map,
  pointA: { lat: number; lon: number },
  pointB: { lat: number; lon: number },
  color: string,
  options?: { selected?: boolean; startFinish?: boolean },
) {
  const line = [lngLatFromPoint(pointA), lngLatFromPoint(pointB)];
  if (options?.startFinish) {
    addLineOverlay(map, line, { color: '#07111c', width: 2.5, opacity: 0.55 });
    addLineOverlay(map, line, { color: '#ffe66d', width: 1.5, opacity: 1 });
    return;
  }

  if (options?.selected) {
    addLineOverlay(map, line, { color: '#07111c', width: 2.5, opacity: 0.55 });
    addLineOverlay(map, line, { color: '#ffe66d', width: 1.5, opacity: 1 });
    return;
  }

  addLineOverlay(map, line, { color: '#07111c', width: 2, opacity: 0.4 });
  addLineOverlay(map, line, { color, width: 1, opacity: 0.95 });
}

function drawCrossLine(
  map: mapboxgl.Map,
  pointA: { lat: number; lon: number },
  pointB: { lat: number; lon: number },
  color: string,
  width = 5,
) {
  const line = [lngLatFromPoint(pointA), lngLatFromPoint(pointB)];
  addLineOverlay(map, line, { color: '#FFFFFF', width: width + 4, opacity: 0.92 });
  addLineOverlay(map, line, { color, width, opacity: 1 });
}

function crossLineMidpoint(line: CrossTrackLineSpec) {
  return {
    lat: (line.pointA.lat + line.pointB.lat) / 2,
    lon: (line.pointA.lon + line.pointB.lon) / 2,
  };
}

function createCatalogCrossLineMarkerElement(line: CrossTrackLineSpec, map: mapboxgl.Map) {
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.flexDirection = 'column';
  wrap.style.alignItems = 'center';
  wrap.style.transform = 'translate(-50%, -50%)';
  wrap.style.pointerEvents = 'auto';

  const lineWrap = document.createElement('div');
  lineWrap.style.position = 'relative';
  lineWrap.style.height = '16px';
  lineWrap.style.display = 'flex';
  lineWrap.style.alignItems = 'center';
  lineWrap.style.justifyContent = 'center';
  lineWrap.style.pointerEvents = 'auto';
  lineWrap.style.cursor = line.draggable ? 'grab' : 'pointer';

  const hitArea = document.createElement('div');
  hitArea.style.position = 'absolute';
  hitArea.style.left = '50%';
  hitArea.style.top = '50%';
  hitArea.style.height = '16px';
  hitArea.style.transform = 'translate(-50%, -50%)';
  hitArea.style.background = 'transparent';
  hitArea.style.pointerEvents = 'auto';

  const underStroke = document.createElement('div');
  underStroke.style.position = 'absolute';
  underStroke.style.left = '50%';
  underStroke.style.top = '50%';
  underStroke.style.height = '2px';
  underStroke.style.background = line.selected ? 'rgba(7,17,28,0.55)' : 'rgba(7,17,28,0.4)';
  underStroke.style.transform = 'translate(-50%, -50%)';
  underStroke.style.pointerEvents = 'none';

  const core = document.createElement('div');
  core.style.position = 'absolute';
  core.style.left = '50%';
  core.style.top = '50%';
  core.style.height = line.selected ? '1.5px' : '1px';
  core.style.background = line.selected ? '#ffe66d' : (line.color ?? '#28c8ff');
  core.style.transform = 'translate(-50%, -50%)';
  core.style.pointerEvents = 'none';

  lineWrap.appendChild(hitArea);
  lineWrap.appendChild(underStroke);
  lineWrap.appendChild(core);
  wrap.appendChild(lineWrap);

  const update = () => {
    const projectedA = map.project([line.pointA.lon, line.pointA.lat]);
    const projectedB = map.project([line.pointB.lon, line.pointB.lat]);
    const length = Math.hypot(projectedB.x - projectedA.x, projectedB.y - projectedA.y);
    const angle =
      (Math.atan2(projectedB.y - projectedA.y, projectedB.x - projectedA.x) * 180) / Math.PI;
    const width = `${Math.max(length, 8)}px`;

    hitArea.style.width = width;
    underStroke.style.width = width;
    core.style.width = width;
    lineWrap.style.width = width;
    lineWrap.style.transform = `rotate(${angle}deg)`;
  };

  update();
  return { element: wrap, lineWrap, update };
}

function createCrossLineMarkerElement(line: CrossTrackLineSpec, map: mapboxgl.Map) {
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.flexDirection = 'column';
  wrap.style.alignItems = 'center';
  wrap.style.gap = '4px';
  wrap.style.transform = 'translate(-50%, -50%)';
  wrap.style.pointerEvents = 'auto';

  const lineWrap = document.createElement('div');
  lineWrap.style.position = 'relative';
  lineWrap.style.height = '18px';
  lineWrap.style.display = 'flex';
  lineWrap.style.alignItems = 'center';
  lineWrap.style.justifyContent = 'center';
  lineWrap.style.pointerEvents = 'auto';
  lineWrap.style.cursor = line.draggable ? 'grab' : 'pointer';

  const halo = document.createElement('div');
  halo.style.position = 'absolute';
  halo.style.left = '50%';
  halo.style.top = '50%';
  halo.style.height = line.selected ? '11px' : '9px';
  halo.style.background = '#FFFFFF';
  halo.style.borderRadius = '999px';
  halo.style.opacity = '0.92';
  halo.style.boxShadow = '0 0 0 2px rgba(7,17,28,0.9), 0 8px 18px rgba(0,0,0,0.28)';
  halo.style.transform = 'translate(-50%, -50%)';
  halo.style.pointerEvents = 'none';

  const core = document.createElement('div');
  core.style.position = 'absolute';
  core.style.left = '50%';
  core.style.top = '50%';
  core.style.height = line.selected ? '6px' : '5px';
  core.style.background = line.color ?? '#28c8ff';
  core.style.borderRadius = '999px';
  core.style.transform = 'translate(-50%, -50%)';
  core.style.pointerEvents = 'none';

  lineWrap.appendChild(halo);
  lineWrap.appendChild(core);
  wrap.appendChild(lineWrap);

  let labelEl: HTMLSpanElement | null = null;
  if (line.label) {
    labelEl = document.createElement('span');
    labelEl.textContent = line.label;
    labelEl.style.padding = '2px 5px';
    labelEl.style.borderRadius = '999px';
    labelEl.style.background = 'rgba(1,13,24,0.78)';
    labelEl.style.border = '1px solid rgba(255,255,255,0.22)';
    labelEl.style.color = '#fff';
    labelEl.style.fontSize = '10px';
    labelEl.style.fontWeight = '800';
    labelEl.style.lineHeight = '1';
    labelEl.style.whiteSpace = 'nowrap';
    labelEl.style.pointerEvents = 'none';
    wrap.appendChild(labelEl);
  }

  const update = () => {
    const projectedA = map.project([line.pointA.lon, line.pointA.lat]);
    const projectedB = map.project([line.pointB.lon, line.pointB.lat]);
    const length = Math.max(
      Math.hypot(projectedB.x - projectedA.x, projectedB.y - projectedA.y),
      12,
    );
    const angle =
      (Math.atan2(projectedB.y - projectedA.y, projectedB.x - projectedA.x) * 180) / Math.PI;

    halo.style.width = `${length}px`;
    core.style.width = `${length}px`;
    lineWrap.style.width = `${length}px`;
    lineWrap.style.transform = `rotate(${angle}deg)`;
  };

  update();
  return { element: wrap, lineWrap, update };
}

function createMarkerElement(marker: TrackMapMarker, scale: number) {
  const baseSize =
    marker.kind === 'braking-soft' || marker.kind === 'braking-hard'
      ? 7
      : marker.kind === 'note'
        ? marker.selected
          ? 14
          : 12
        : marker.kind === 'footage-start'
          ? 18
          : marker.kind === 'sector'
            ? marker.selected
              ? 19
              : 15
            : marker.selected
              ? 16
              : 11;
  const size = baseSize * scale;

  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.alignItems = 'center';
  wrap.style.gap = '4px';
  wrap.style.transform = 'translate(-50%, -50%)';
  wrap.style.pointerEvents = 'auto';

  const dot = document.createElement('span');
  dot.style.width = `${size}px`;
  dot.style.height = `${size}px`;
  dot.style.borderRadius = '999px';
  dot.style.background = marker.color;
  dot.style.border = `${marker.selected ? 3 : 2}px solid #fff`;
  dot.style.boxShadow = '0 0 0 2px rgba(7,17,28,0.9), 0 8px 18px rgba(0,0,0,0.38)';
  wrap.appendChild(dot);

  if (
    marker.kind !== 'sector-boundary' ? marker.label : marker.selected ? marker.label : undefined
  ) {
    const label = document.createElement('span');
    label.textContent = marker.label;
    label.style.padding = '2px 5px';
    label.style.borderRadius = '999px';
    label.style.background = 'rgba(1,13,24,0.78)';
    label.style.border = '1px solid rgba(255,255,255,0.22)';
    label.style.color = '#fff';
    label.style.fontSize = `${Math.max(10, Math.round(size * 0.58))}px`;
    label.style.fontWeight = '800';
    label.style.lineHeight = '1';
    label.style.whiteSpace = 'nowrap';
    wrap.appendChild(label);
  }

  return wrap;
}

type ColoredLngLatPath = {
  color: string;
  coordinates: Array<[number, number]>;
};

function mergeColoredLngLatSegments(
  group: TrackMapPoint[],
  minSpeed: number,
  maxSpeed: number,
): ColoredLngLatPath[] {
  const paths: ColoredLngLatPath[] = [];

  for (let index = 1; index < group.length; index += 1) {
    const previous = group[index - 1]!;
    const current = group[index]!;
    const speed = (previous.speedKph + current.speedKph) / 2;
    const color = speedColorBrakeToFlatOut(speed, minSpeed, maxSpeed);
    const start = lngLatFromPoint(previous);
    const end = lngLatFromPoint(current);
    const last = paths.at(-1);

    if (last && last.color === color) {
      last.coordinates.push(end);
      continue;
    }

    paths.push({ color, coordinates: [start, end] });
  }

  return paths;
}

function addColoredLineOverlay(
  map: mapboxgl.Map,
  paths: ColoredLngLatPath[],
  width: number,
  opacity: number,
) {
  if (paths.length === 0) {
    return;
  }

  const sourceId = nextOverlayId('colored-line-src');
  const layerId = nextOverlayId('colored-line');
  map.addSource(sourceId, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: paths.map((path) => ({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: path.coordinates },
        properties: { color: path.color },
      })),
    },
  });
  map.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': ['get', 'color'],
      'line-width': width,
      'line-opacity': opacity,
    },
  });
}

function replayCursorMarkerContent(cursor: MapReplayCursor) {
  const dotSize = cursor.follow ? 30 : 28;
  const wrap = document.createElement('div');
  wrap.style.transform = 'translate(-50%, -50%)';
  wrap.style.pointerEvents = 'none';

  if (cursor.avatarUrl) {
    const frame = document.createElement('span');
    frame.style.display = 'block';
    frame.style.width = `${dotSize}px`;
    frame.style.height = `${dotSize}px`;
    frame.style.borderRadius = '999px';
    frame.style.background = '#020A12';
    frame.style.border = `2px solid ${cursor.color}`;
    frame.style.borderStyle = cursor.identityVariant ? 'dashed' : 'solid';
    frame.style.boxShadow = '0 0 0 2px rgba(7,17,28,0.55), 0 5px 14px rgba(0,0,0,0.48)';
    frame.style.overflow = 'hidden';

    const image = document.createElement('img');
    image.src = cursor.avatarUrl;
    image.alt = '';
    image.style.display = 'block';
    image.style.width = '100%';
    image.style.height = '100%';
    image.style.objectFit = 'cover';
    frame.appendChild(image);
    wrap.appendChild(frame);
  } else {
    const dot = document.createElement('span');
    dot.style.display = 'grid';
    dot.style.placeItems = 'center';
    dot.style.width = `${dotSize}px`;
    dot.style.height = `${dotSize}px`;
    dot.style.borderRadius = '999px';
    dot.style.background = '#020A12';
    dot.style.border = `2px ${cursor.identityVariant ? 'dashed' : 'solid'} ${cursor.color}`;
    dot.style.color = '#FFFFFF';
    dot.style.fontSize = '10px';
    dot.style.fontWeight = '900';
    dot.style.lineHeight = '1';
    dot.style.boxShadow = '0 0 0 1px rgba(7,17,28,0.55), 0 4px 12px rgba(0,0,0,0.45)';
    const initials = cursor.initials?.trim();
    dot.textContent = initials && initials !== '??' ? initials : cursor.label?.trim() || '??';
    wrap.appendChild(dot);
  }

  return wrap;
}

function mountCenterFollowCursor(overlay: HTMLElement, cursor: MapReplayCursor) {
  const element = replayCursorMarkerContent(cursor);
  element.style.cssText =
    'position:absolute;left:50%;top:50%;transform:translate3d(-50%,-50%,0);pointer-events:none;will-change:transform;contain:layout style paint;';
  overlay.appendChild(element);
  return element;
}

function mountProjectedReplayCursor(overlay: HTMLElement, cursor: MapReplayCursor) {
  const positioner = document.createElement('div');
  positioner.style.cssText =
    'position:absolute;left:0;top:0;pointer-events:none;will-change:transform;contain:layout style;';
  positioner.appendChild(replayCursorMarkerContent(cursor));
  overlay.appendChild(positioner);
  return positioner;
}

function clearReplayCursorOverlay(
  overlay: HTMLElement | null,
  cursorElements: Map<string, HTMLElement>,
  centerFollowCursor: { current: HTMLElement | null },
) {
  cursorElements.clear();
  centerFollowCursor.current = null;
  overlay?.replaceChildren();
}

function resolveReplayCursorPosition(
  cursor: MapReplayCursor,
  timecodeMs: number,
  motionCaches: Map<string, { points: TrackMapPoint[]; cache: ReplayMotionCache }>,
  interpHints: Map<string, InterpolateMapPointHint>,
) {
  const cached = motionCaches.get(cursor.id);
  if (cached && cached.points === cursor.points) {
    return sampleReplayMotionCache(cached.cache, timecodeMs);
  }

  const built = buildReplayMotionCache(cursor.points);
  if (built) {
    motionCaches.set(cursor.id, { points: cursor.points, cache: built });
    sessionMapDebug('replay motion cache built lazily', {
      cursorId: cursor.id,
      sampleCount: built.count,
    });
    return sampleReplayMotionCache(built, timecodeMs);
  }

  const hint = interpHints.get(cursor.id) ?? { index: 0 };
  interpHints.set(cursor.id, hint);
  const sample = interpolateMapPointAtTimeWithHint(cursor.points, timecodeMs, hint);
  return sample ? { lat: sample.lat, lon: sample.lon } : null;
}

function trackFitKey(resolvedTrackGroups: TrackMapPoint[][], points: TrackMapPoint[]) {
  const primaryGroup = resolvedTrackGroups[0] ?? points;
  const first = primaryGroup[0];
  const last = primaryGroup.at(-1);
  return [
    resolvedTrackGroups.length,
    points.length,
    first?.timecodeMs ?? 0,
    last?.timecodeMs ?? 0,
    first ? `${first.lat.toFixed(4)}:${first.lon.toFixed(4)}` : '0:0',
    last ? `${last.lat.toFixed(4)}:${last.lon.toFixed(4)}` : '0:0',
  ].join(':');
}

function collectFitCoords(
  resolvedTrackGroups: TrackMapPoint[][],
  points: TrackMapPoint[],
  resolvedReferenceTrackGroups: TrackMapPoint[][],
  mapMarkers: TrackMapMarker[],
) {
  if (resolvedTrackGroups.length > 1) {
    return [
      ...resolvedTrackGroups.flatMap((group) => group.map((point) => lngLatFromPoint(point))),
      ...resolvedReferenceTrackGroups.flatMap((group) =>
        group.map((point) => lngLatFromPoint(point)),
      ),
      ...mapMarkers.map((marker) => lngLatFromPoint(marker)),
    ];
  }

  return [
    ...points.map((point) => lngLatFromPoint(point)),
    ...resolvedReferenceTrackGroups.flatMap((group) =>
      group.map((point) => lngLatFromPoint(point)),
    ),
    ...mapMarkers.map((marker) => lngLatFromPoint(marker)),
  ];
}

export function MapboxTrackMap({
  accessToken,
  points,
  trackGroups,
  referenceTrackGroups = [],
  speedRange,
  height = 292,
  title,
  fill = false,
  resetViewKey,
  onMapClick,
  onMapLatLngClick,
  onMapLatLngHover,
  crosshairCursor = false,
  freehandDrawActive = false,
  onMapLatLngDrawStroke,
  onMapLatLngDrawEnd,
  highlightLines = [],
  onMapMarkerClick,
  onMapMarkerDragEnd,
  onOverlaysReady,
  startFinishLine,
  crossTrackLines = [],
  mapMarkers = [],
  mapMarkerScale = 1,
  heatLineWidth = 4.5,
  heatLineHaloWidth = 9,
  fitBoundsPadding = 68,
  onTilesReady,
  onLoadError,
  mapViewport,
  autoFitBounds = true,
  followMapViewport = true,
  referenceTrackStyle = 'default',
  referenceTrackGuideColors = [],
  detailedCircuitPreview = null,
  selectedDetailedFeatureId,
  persistentDetailedFeatureIds = [],
  interactiveSectionDividers = false,
  selectedSectionBoundaryLine = null,
  corridorOutlineRings = [],
  showLapLines = true,
  idealPoints = [],
  showIdealLine = false,
  cursorTimecodeMs = null,
  replayCursors,
  followReplayCursor = false,
  resolveReplayCursorTimecodeMsRef,
}: MapboxTrackMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cursorOverlayRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const cursorElementsRef = useRef<Map<string, HTMLElement>>(new Map());
  const centerFollowCursorRef = useRef<HTMLElement | null>(null);
  const centerFollowCursorKeyRef = useRef<string | null>(null);
  const cursorInterpHintsRef = useRef<Map<string, InterpolateMapPointHint>>(new Map());
  const replayMotionCachesRef = useRef<
    Map<string, { points: TrackMapPoint[]; cache: ReplayMotionCache }>
  >(new Map());
  const lastFollowCenterUpdateRef = useRef(0);
  const crossLineMarkersRef = useRef<Array<{ marker: mapboxgl.Marker; update: () => void }>>([]);
  const onTilesReadyRef = useRef(onTilesReady);
  const onLoadErrorRef = useRef(onLoadError);
  const onMapLatLngClickRef = useRef(onMapLatLngClick);
  const onMapLatLngHoverRef = useRef(onMapLatLngHover);
  const onMapLatLngDrawStrokeRef = useRef(onMapLatLngDrawStroke);
  const onMapLatLngDrawEndRef = useRef(onMapLatLngDrawEnd);
  const onMapMarkerClickRef = useRef(onMapMarkerClick);
  const onMapMarkerDragEndRef = useRef(onMapMarkerDragEnd);
  const onOverlaysReadyRef = useRef(onOverlaysReady);
  const freehandDrawActiveRef = useRef(freehandDrawActive);
  const mapViewportRef = useRef(mapViewport);
  const resetViewKeyRef = useRef(resetViewKey);
  const lastFlownViewportRef = useRef<string | null>(null);
  const sessionFitKeyRef = useRef('');
  const lapFitKeyRef = useRef('');
  const lastFittedResetViewKeyRef = useRef<string | number | undefined>(undefined);
  const overlayFingerprintRef = useRef<string | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const resolvedTrackGroups = useMemo(
    () =>
      (trackGroups?.length ? trackGroups : points.length >= 2 ? [points] : []).flatMap((group) =>
        splitContinuousTelemetryPoints(group),
      ),
    [points, trackGroups],
  );
  const resolvedReferenceTrackGroups = useMemo(
    () => referenceTrackGroups.filter((group) => group.length >= 2),
    [referenceTrackGroups],
  );
  const resolvedReferenceTrackGuideColors = useMemo(
    () => referenceTrackGuideColors.slice(0, resolvedReferenceTrackGroups.length),
    [referenceTrackGuideColors, resolvedReferenceTrackGroups.length],
  );
  const resolvedCursors = useMemo((): MapReplayCursor[] => {
    if (replayCursors?.length) {
      return replayCursors;
    }

    if (cursorTimecodeMs !== null && points.length >= 2) {
      return [
        {
          id: 'primary',
          points,
          timecodeMs: cursorTimecodeMs,
          color: '#A855F7',
          follow: true,
        },
      ];
    }

    return [];
  }, [cursorTimecodeMs, points, replayCursors]);
  const replayActive = resolvedCursors.some((cursor) => cursor.timecodeMs !== null);

  const replayCursorsRef = useRef(resolvedCursors);
  const resolveReplayCursorTimecodeMsRefRef = useRef(resolveReplayCursorTimecodeMsRef);
  const followReplayCursorRef = useRef(followReplayCursor);
  const replayFollowZoomAppliedRef = useRef(false);
  replayCursorsRef.current = resolvedCursors;
  resolveReplayCursorTimecodeMsRefRef.current = resolveReplayCursorTimecodeMsRef;
  followReplayCursorRef.current = followReplayCursor;

  onTilesReadyRef.current = onTilesReady;
  onLoadErrorRef.current = onLoadError;
  onMapLatLngClickRef.current = onMapLatLngClick;
  onMapLatLngHoverRef.current = onMapLatLngHover;
  onMapLatLngDrawStrokeRef.current = onMapLatLngDrawStroke;
  onMapLatLngDrawEndRef.current = onMapLatLngDrawEnd;
  onMapMarkerClickRef.current = onMapMarkerClick;
  onMapMarkerDragEndRef.current = onMapMarkerDragEnd;
  onOverlaysReadyRef.current = onOverlaysReady;
  freehandDrawActiveRef.current = freehandDrawActive;
  mapViewportRef.current = mapViewport;
  resetViewKeyRef.current = resetViewKey;

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const start =
      points[0] ??
      (mapViewportRef.current
        ? { lat: mapViewportRef.current.lat, lon: mapViewportRef.current.lon }
        : null);
    if (!start) {
      return;
    }

    mapboxgl.accessToken = accessToken;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [start.lon, start.lat],
      zoom: mapViewportRef.current?.zoom ?? FOLLOW_ZOOM,
      attributionControl: false,
      cooperativeGestures: true,
      maxZoom: 22,
      antialias: false,
      fadeDuration: 0,
      renderWorldCopies: false,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    const handleReady = () => {
      sessionMapDebug('Mapbox map load event', {
        pointCount: points.length,
        replayCursorCount: replayCursorsRef.current.length,
      });
      setMapReady(true);
      onTilesReadyRef.current?.();
    };

    const handleError = (event: mapboxgl.ErrorEvent) => {
      const message = event.error?.message ?? 'Mapbox failed to load.';
      sessionMapDebug('Mapbox map error', { message });
      setLoadError(message);
      onLoadErrorRef.current?.(message);
    };

    map.on('load', handleReady);
    map.on('error', handleError);

    return () => {
      map.off('load', handleReady);
      map.off('error', handleError);
      for (const marker of markersRef.current) {
        marker.remove();
      }
      markersRef.current = [];
      for (const crossLineMarker of crossLineMarkersRef.current) {
        crossLineMarker.marker.remove();
      }
      crossLineMarkersRef.current = [];
      clearReplayCursorOverlay(
        cursorOverlayRef.current,
        cursorElementsRef.current,
        centerFollowCursorRef,
      );
      replayMotionCachesRef.current.clear();
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [accessToken]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !mapViewport || !followMapViewport) {
      return;
    }

    const viewportKey = `${mapViewport.lat}:${mapViewport.lon}:${mapViewport.zoom ?? FOLLOW_ZOOM}`;
    if (lastFlownViewportRef.current === viewportKey) {
      return;
    }

    lastFlownViewportRef.current = viewportKey;
    map.flyTo({
      center: [mapViewport.lon, mapViewport.lat],
      zoom: mapViewport.zoom ?? FOLLOW_ZOOM,
      duration: 300,
    });
  }, [followMapViewport, mapReady, mapViewport?.lat, mapViewport?.lon, mapViewport?.zoom]);

  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!mapReady || !map || !container || typeof ResizeObserver === 'undefined') {
      return;
    }

    let animationFrame: number | null = null;
    let previousWidth = -1;
    let previousHeight = -1;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }
      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);
      if (width === previousWidth && height === previousHeight) {
        return;
      }
      previousWidth = width;
      previousHeight = height;
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        if (mapRef.current === map) {
          map.resize();
        }
      });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !autoFitBounds || resetViewKey === undefined) {
      return;
    }

    const fitKey = trackFitKey(resolvedTrackGroups, points);
    const hasReferenceTracks = resolvedReferenceTrackGroups.length > 0;
    const viewResetRequested = lastFittedResetViewKeyRef.current !== resetViewKey;

    if (viewResetRequested) {
      sessionFitKeyRef.current = '';
      lapFitKeyRef.current = '';
    }

    const fitCoords = collectFitCoords(
      resolvedTrackGroups,
      points,
      resolvedReferenceTrackGroups,
      mapMarkers,
    );
    if (fitCoords.length < 2) {
      return;
    }

    const shouldFitMultipleGroups =
      resolvedTrackGroups.length > 1 && (sessionFitKeyRef.current !== fitKey || viewResetRequested);
    const shouldFitSingleGroup =
      resolvedTrackGroups.length <= 1 &&
      (points.length >= 2 || hasReferenceTracks) &&
      (lapFitKeyRef.current !== fitKey || viewResetRequested);

    if (!shouldFitMultipleGroups && !shouldFitSingleGroup) {
      return;
    }

    const bounds = fitCoords.reduce(
      (nextBounds, coordinate) => nextBounds.extend(coordinate),
      new mapboxgl.LngLatBounds(fitCoords[0]!, fitCoords[0]!),
    );
    map.fitBounds(bounds, { padding: fitBoundsPadding, maxZoom: FOLLOW_ZOOM, duration: 300 });

    if (resolvedTrackGroups.length > 1) {
      sessionFitKeyRef.current = fitKey;
    } else {
      lapFitKeyRef.current = fitKey;
    }
    lastFittedResetViewKeyRef.current = resetViewKey;
    lastFlownViewportRef.current = null;
  }, [
    autoFitBounds,
    fitBoundsPadding,
    mapMarkers,
    mapReady,
    points,
    resetViewKey,
    resolvedReferenceTrackGroups,
    resolvedTrackGroups,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    if (freehandDrawActive || !autoFitBounds) {
      map.doubleClickZoom.disable();
      map.scrollZoom.enable();
    } else {
      map.doubleClickZoom.enable();
    }
  }, [autoFitBounds, freehandDrawActive, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    const handleClick = (event: mapboxgl.MapMouseEvent) => {
      if (freehandDrawActiveRef.current) {
        return;
      }
      onMapLatLngClickRef.current?.({ lat: event.lngLat.lat, lon: event.lngLat.lng });
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !freehandDrawActive) {
      return;
    }

    let drawing = false;

    const stopDrawing = () => {
      if (drawing) {
        onMapLatLngDrawEndRef.current?.();
      }
      drawing = false;
      map.dragPan.enable();
    };

    const strokeAt = (event: mapboxgl.MapMouseEvent) => {
      onMapLatLngDrawStrokeRef.current?.({ lat: event.lngLat.lat, lon: event.lngLat.lng });
    };

    const onMouseDown = (event: mapboxgl.MapMouseEvent) => {
      if (event.originalEvent.button !== 0) {
        return;
      }
      drawing = true;
      map.dragPan.disable();
      strokeAt(event);
    };

    const onMouseMove = (event: mapboxgl.MapMouseEvent) => {
      if (!drawing) {
        return;
      }
      strokeAt(event);
    };

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', stopDrawing);
    map.on('mouseleave', stopDrawing);
    const canvas = map.getCanvas();
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }

    return () => {
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', stopDrawing);
      map.off('mouseleave', stopDrawing);
      map.dragPan.enable();
      if (canvas) {
        canvas.style.cursor = '';
      }
    };
  }, [freehandDrawActive, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || freehandDrawActive) {
      return;
    }

    const canvas = map.getCanvas();
    if (canvas) {
      canvas.style.cursor = crosshairCursor ? 'crosshair' : '';
    }
    return () => {
      if (canvas) {
        canvas.style.cursor = '';
      }
    };
  }, [crosshairCursor, freehandDrawActive, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    const handleMove = (event: mapboxgl.MapMouseEvent) => {
      onMapLatLngHoverRef.current?.({ lat: event.lngLat.lat, lon: event.lngLat.lng });
    };
    const handleLeave = () => {
      onMapLatLngHoverRef.current?.(null);
    };

    map.on('mousemove', handleMove);
    map.on('mouseleave', handleLeave);
    return () => {
      map.off('mousemove', handleMove);
      map.off('mouseleave', handleLeave);
    };
  }, [mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !map.isStyleLoaded()) {
      return;
    }

    const first = points[0];
    const last = points.at(-1);
    const fingerprint = [
      resolvedTrackGroups.length,
      resolvedReferenceTrackGroups.length,
      trackGroupFingerprint(resolvedTrackGroups),
      trackGroupFingerprint(resolvedReferenceTrackGroups),
      referenceTrackStyle,
      resolvedReferenceTrackGuideColors.map((colors) => colors.line).join('|'),
      points.length,
      first?.timecodeMs ?? 0,
      last?.timecodeMs ?? 0,
      showLapLines ? 1 : 0,
      showIdealLine ? 1 : 0,
      idealPoints.length,
      speedRange?.min ?? 0,
      speedRange?.max ?? 0,
      heatLineWidth,
      heatLineHaloWidth,
      detailedCircuitPreview?.features.length ?? 0,
      selectedDetailedFeatureId ?? '',
      persistentDetailedFeatureIds.join('|'),
      highlightLines
        .map((line) => {
          const firstPoint = line.points[0];
          const lastPoint = line.points.at(-1);
          return `${line.id}:${line.points.length}:${firstPoint?.lat}:${firstPoint?.lon}:${lastPoint?.lat}:${lastPoint?.lon}:${line.color}:${line.width}:${line.opacity}:${line.dash?.join(',')}`;
        })
        .join('|'),
      corridorOutlineRings.length,
      crossTrackLines
        .map((line) =>
          [
            line.id,
            line.pointA.lat,
            line.pointA.lon,
            line.pointB.lat,
            line.pointB.lon,
            line.draggable ? 1 : 0,
            line.selected ? 1 : 0,
            line.label ?? '',
            line.lineStyle ?? 'pill',
          ].join(':'),
        )
        .join('|'),
      mapMarkers
        .map(
          (marker) =>
            `${marker.id}:${marker.kind}:${marker.lat.toFixed(5)}:${marker.lon.toFixed(5)}:${marker.selected ? 1 : 0}`,
        )
        .join('|'),
      startFinishLine
        ? `${startFinishLine.pointA.lat}:${startFinishLine.pointA.lon}:${startFinishLine.pointB.lat}:${startFinishLine.pointB.lon}`
        : '',
    ].join(':');

    const viewResetRequested = resetViewKeyRef.current !== resetViewKey;
    if (viewResetRequested) {
      resetViewKeyRef.current = resetViewKey;
    }

    if (overlayFingerprintRef.current === fingerprint && !viewResetRequested) {
      return;
    }

    overlayFingerprintRef.current = fingerprint;

    clearPaddiqOverlays(map);
    for (const marker of markersRef.current) {
      marker.remove();
    }
    markersRef.current = [];
    for (const crossLineMarker of crossLineMarkersRef.current) {
      crossLineMarker.marker.remove();
    }
    crossLineMarkersRef.current = [];

    const updateCrossLineMarkers = () => {
      for (const crossLineMarker of crossLineMarkersRef.current) {
        crossLineMarker.update();
      }
    };
    map.on('move', updateCrossLineMarkers);
    map.on('zoom', updateCrossLineMarkers);

    if (detailedCircuitPreview) {
      drawDetailedCircuitPreview(map, detailedCircuitPreview, DEFAULT_CIRCUIT_PREVIEW_LAYERS, {
        selectedBoundaryLine: selectedSectionBoundaryLine ?? undefined,
        interactiveSectionDividers,
        selectedFeatureId: selectedDetailedFeatureId,
        persistentFeatureIds: persistentDetailedFeatureIds,
      });
    } else if (corridorOutlineRings.length > 0) {
      drawCorridorOutlineRings(map, corridorOutlineRings);
    }

    drawReferenceTrackOutlines(
      map,
      resolvedReferenceTrackGroups,
      referenceTrackStyle,
      resolvedReferenceTrackGuideColors,
    );
    if (detailedCircuitPreview) {
      drawDetailedCircuitKerbs(map, detailedCircuitPreview);
    }
    if (showIdealLine && idealPoints.length > 1) {
      const idealPath = lngLatPath(idealPoints);
      addLineOverlay(map, idealPath, { color: '#07111C', width: 5.5, opacity: 0.82 });
      addLineOverlay(map, idealPath, { color: '#38BDF8', width: 2, opacity: 0.9, dash: [2, 8] });
    }

    if (showLapLines) {
      for (const group of resolvedTrackGroups) {
        const trackPath = lngLatPath(group);
        const groupRange = speedRange ?? trackSpeedRange(group) ?? undefined;
        const speeds = group.map((point) => point.speedKph);
        const minSpeed = groupRange?.min ?? Math.min(...speeds);
        const maxSpeed = groupRange?.max ?? Math.max(...speeds);

        addLineOverlay(map, trackPath, {
          color: '#07111C',
          width: heatLineHaloWidth,
          opacity: 0.92,
        });
        addLineOverlay(map, trackPath, {
          color: '#FFFFFF',
          width: heatLineWidth + 1.5,
          opacity: 0.95,
        });

        addColoredLineOverlay(
          map,
          mergeColoredLngLatSegments(group, minSpeed, maxSpeed),
          heatLineWidth,
          0.82,
        );
      }
    }

    for (const line of highlightLines) {
      addLineOverlay(map, lngLatPath(line.points), {
        color: line.color ?? '#F97316',
        width: line.width ?? 11,
        opacity: line.opacity ?? 0.72,
        dash: line.dash,
      });
    }

    if (startFinishLine) {
      drawCrossLine(map, startFinishLine.pointA, startFinishLine.pointB, '#22C55E');
    }

    for (const line of crossTrackLines) {
      if (line.lineStyle === 'catalog') {
        const interactive = line.draggable || line.label || line.selected;
        if (interactive) {
          const { element, lineWrap, update } = createCatalogCrossLineMarkerElement(line, map);
          const mid = crossLineMidpoint(line);
          const draggable = Boolean(line.draggable && onMapMarkerDragEndRef.current);

          if (onMapMarkerClickRef.current) {
            element.addEventListener('click', (event) => {
              event.stopPropagation();
              onMapMarkerClickRef.current?.(line.id);
            });
          }

          const mapMarker = new mapboxgl.Marker({ element, draggable, anchor: 'center' })
            .setLngLat([mid.lon, mid.lat])
            .addTo(map);

          if (draggable) {
            lineWrap.style.cursor = 'grab';
            mapMarker.on('dragstart', () => {
              lineWrap.style.cursor = 'grabbing';
            });
            mapMarker.on('dragend', () => {
              lineWrap.style.cursor = 'grab';
              const lngLat = mapMarker.getLngLat();
              onMapMarkerDragEndRef.current?.(line.id, { lat: lngLat.lat, lon: lngLat.lng });
            });
          }

          crossLineMarkersRef.current.push({ marker: mapMarker, update });
        } else {
          drawCatalogDividerLine(map, line.pointA, line.pointB, line.color ?? '#28c8ff', {
            selected: line.selected,
          });
        }
        continue;
      }

      const interactive = line.draggable || line.label || line.selected;
      if (interactive) {
        const { element, lineWrap, update } = createCrossLineMarkerElement(line, map);
        const mid = crossLineMidpoint(line);
        const draggable = Boolean(line.draggable && onMapMarkerDragEndRef.current);

        if (onMapMarkerClickRef.current) {
          element.addEventListener('click', (event) => {
            event.stopPropagation();
            onMapMarkerClickRef.current?.(line.id);
          });
        }

        const mapMarker = new mapboxgl.Marker({ element, draggable })
          .setLngLat([mid.lon, mid.lat])
          .addTo(map);

        if (draggable) {
          lineWrap.style.cursor = 'grab';
          mapMarker.on('dragstart', () => {
            lineWrap.style.cursor = 'grabbing';
          });
          mapMarker.on('dragend', () => {
            lineWrap.style.cursor = 'grab';
            const lngLat = mapMarker.getLngLat();
            onMapMarkerDragEndRef.current?.(line.id, { lat: lngLat.lat, lon: lngLat.lng });
          });
        }

        crossLineMarkersRef.current.push({ marker: mapMarker, update });
        continue;
      }

      drawCrossLine(map, line.pointA, line.pointB, line.color ?? '#28c8ff');
    }

    for (const marker of mapMarkers) {
      const element = createMarkerElement(marker, mapMarkerScale);
      const draggable =
        (marker.kind === 'note' || marker.kind === 'section-boundary') &&
        Boolean(onMapMarkerDragEndRef.current);

      if (
        onMapMarkerClickRef.current &&
        (marker.kind === 'note' || marker.kind === 'footage-start')
      ) {
        element.style.cursor = draggable ? 'grab' : 'pointer';
        element.addEventListener('click', (event) => {
          event.stopPropagation();
          onMapMarkerClickRef.current?.(marker.id);
        });
      }

      const mapMarker = new mapboxgl.Marker({ element, draggable })
        .setLngLat([marker.lon, marker.lat])
        .addTo(map);

      if (draggable) {
        mapMarker.on('dragstart', () => {
          element.style.cursor = 'grabbing';
        });
        mapMarker.on('dragend', () => {
          element.style.cursor = 'grab';
          const lngLat = mapMarker.getLngLat();
          onMapMarkerDragEndRef.current?.(marker.id, { lat: lngLat.lat, lon: lngLat.lng });
        });
      }

      markersRef.current.push(mapMarker);
    }

    map.triggerRepaint();
    const overlaysReadyFrame = window.requestAnimationFrame(() => {
      onOverlaysReadyRef.current?.();
    });

    return () => {
      window.cancelAnimationFrame(overlaysReadyFrame);
      map.off('move', updateCrossLineMarkers);
      map.off('zoom', updateCrossLineMarkers);
    };
  }, [
    corridorOutlineRings,
    crossTrackLines,
    detailedCircuitPreview,
    selectedDetailedFeatureId,
    persistentDetailedFeatureIds,
    interactiveSectionDividers,
    selectedSectionBoundaryLine,
    heatLineHaloWidth,
    heatLineWidth,
    highlightLines,
    mapMarkerScale,
    mapMarkers,
    idealPoints,
    showIdealLine,
    mapReady,
    points,
    referenceTrackStyle,
    resetViewKey,
    resolvedReferenceTrackGuideColors,
    resolvedReferenceTrackGroups,
    resolvedTrackGroups,
    showLapLines,
    speedRange,
    startFinishLine,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) {
      return;
    }

    if (!replayActive) {
      clearReplayCursorOverlay(
        cursorOverlayRef.current,
        cursorElementsRef.current,
        centerFollowCursorRef,
      );
      cursorInterpHintsRef.current.clear();
      replayMotionCachesRef.current.clear();
      centerFollowCursorKeyRef.current = null;
      replayFollowZoomAppliedRef.current = false;
      return;
    }

    const overlay = cursorOverlayRef.current;
    if (!overlay) {
      return;
    }

    const cursorIds = new Set(replayCursorsRef.current.map((cursor) => cursor.id));
    for (const id of cursorInterpHintsRef.current.keys()) {
      if (!cursorIds.has(id)) {
        cursorInterpHintsRef.current.delete(id);
      }
    }
    for (const id of replayMotionCachesRef.current.keys()) {
      if (!cursorIds.has(id)) {
        replayMotionCachesRef.current.delete(id);
      }
    }

    const updateCursors = () => {
      const activeMap = mapRef.current;
      const cursors = replayCursorsRef.current;
      const following = followReplayCursorRef.current;
      const overlayEl = cursorOverlayRef.current;

      if (!activeMap || !overlayEl || cursors.length === 0) {
        return;
      }

      const activeIds = new Set<string>();
      let followCenter: [number, number] | null = null;
      let followCursor: MapReplayCursor | null = null;

      for (const cursor of cursors) {
        const resolve = resolveReplayCursorTimecodeMsRefRef.current?.current;
        const timecodeMs = resolve?.(cursor) ?? cursor.timecodeMs;
        if (timecodeMs === null || cursor.points.length === 0) {
          continue;
        }

        activeIds.add(cursor.id);
        const cursorPoint = resolveReplayCursorPosition(
          cursor,
          timecodeMs,
          replayMotionCachesRef.current,
          cursorInterpHintsRef.current,
        );
        if (!cursorPoint) {
          continue;
        }

        if (following && cursor.follow) {
          cursorElementsRef.current.get(cursor.id)?.remove();
          cursorElementsRef.current.delete(cursor.id);
          followCenter = [cursorPoint.lon, cursorPoint.lat];
          followCursor = cursor;
          continue;
        }

        let element = cursorElementsRef.current.get(cursor.id);
        if (!element) {
          element = mountProjectedReplayCursor(overlayEl, cursor);
          cursorElementsRef.current.set(cursor.id, element);
        }
        const projected = activeMap.project([cursorPoint.lon, cursorPoint.lat]);
        element.style.transform = `translate3d(${projected.x}px,${projected.y}px,0)`;
      }

      if (following && followCursor && followCenter) {
        const followKey = `${followCursor.id}:${followCursor.color}:${followCursor.avatarUrl ?? ''}:${followCursor.initials ?? ''}:${followCursor.label ?? ''}`;
        if (centerFollowCursorKeyRef.current !== followKey || !centerFollowCursorRef.current) {
          centerFollowCursorRef.current?.remove();
          centerFollowCursorRef.current = mountCenterFollowCursor(overlayEl, followCursor);
          centerFollowCursorKeyRef.current = followKey;
        }
        centerFollowCursorRef.current!.style.display = '';
        const now = performance.now();
        if (now - lastFollowCenterUpdateRef.current >= FOLLOW_CAMERA_MIN_INTERVAL_MS) {
          lastFollowCenterUpdateRef.current = now;
          activeMap.setCenter(followCenter);
        }
      } else {
        centerFollowCursorRef.current?.remove();
        centerFollowCursorRef.current = null;
        centerFollowCursorKeyRef.current = null;
        lastFollowCenterUpdateRef.current = 0;
      }

      for (const [id, element] of cursorElementsRef.current) {
        if (!activeIds.has(id)) {
          element.remove();
          cursorElementsRef.current.delete(id);
        }
      }
    };

    if (
      followReplayCursor &&
      resolvedTrackGroups.length <= 1 &&
      !replayFollowZoomAppliedRef.current
    ) {
      map.setZoom(FOLLOW_ZOOM);
      replayFollowZoomAppliedRef.current = true;
    }

    if (!followReplayCursor) {
      replayFollowZoomAppliedRef.current = false;
    }

    updateCursors();
    const unsubscribePlayhead = subscribeToPlayheadTicks(updateCursors);
    const updatePausedCursors = () => {
      if (!followReplayCursorRef.current) {
        updateCursors();
      }
    };
    map.on('move', updatePausedCursors);
    map.on('resize', updateCursors);
    return () => {
      unsubscribePlayhead();
      map.off('move', updatePausedCursors);
      map.off('resize', updateCursors);
    };
  }, [followReplayCursor, mapReady, replayActive, resolvedTrackGroups.length]);

  const titleHeight = title ? 34 : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: fill ? '100%' : height,
        minHeight: fill ? 260 : height,
        bgcolor: '#020A12',
      }}
    >
      {title ? (
        <Typography
          sx={{
            px: 1.25,
            py: 0.75,
            fontSize: '0.78rem',
            fontWeight: 800,
            color: '#E2E8F0',
            borderBottom: '1px solid rgba(36,62,82,0.55)',
            height: titleHeight,
          }}
        >
          {title}
        </Typography>
      ) : null}
      {loadError ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            px: 2,
            textAlign: 'center',
            zIndex: 2,
          }}
        >
          <Typography sx={{ fontSize: '0.85rem', color: '#F8FAFC', fontWeight: 800, mb: 0.75 }}>
            Mapbox could not load
          </Typography>
          <Typography sx={{ fontSize: '0.76rem', color: '#94A3B8', lineHeight: 1.55 }}>
            {loadError}
          </Typography>
        </Box>
      ) : null}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: title ? `calc(100% - ${titleHeight}px)` : '100%',
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            width: '100%',
            height: '100%',
            cursor: freehandDrawActive ? 'crosshair' : undefined,
          }}
        />
        <Box
          ref={cursorOverlayRef}
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      </Box>
    </Box>
  );
}
